import {
  Color,
  DirectionalLight,
  HemisphereLight,
  AmbientLight,
  type Scene,
} from 'three';
import { PALETTE } from '@/config';
import { lerp } from '@/utils/math';
import { IS_TOUCH } from '@/utils/device';

/**
 * A soft, gallery-lit rig. The look is intentionally flat and diffuse — a museum
 * skylight, not a dramatic sunset — with one low-angle key light to carve gentle
 * shadows between buildings so the massing reads. Everything here is exposed to the
 * environment/mood system so vitality can warm or drain the whole scene.
 */
export class Lighting {
  readonly sun: DirectionalLight;
  readonly hemi: HemisphereLight;
  readonly ambient: AmbientLight;

  private readonly warmSky = new Color(PALETTE.moodAlive);
  private readonly coolSky = new Color(PALETTE.moodBleak);

  constructor(scene: Scene) {
    this.hemi = new HemisphereLight(PALETTE.sky, PALETTE.ground, 0.9);
    scene.add(this.hemi);

    this.ambient = new AmbientLight(0xffffff, 0.35);
    scene.add(this.ambient);

    this.sun = new DirectionalLight(0xfff6e8, 1.15);
    this.sun.position.set(60, 90, 40);
    this.sun.castShadow = true;
    // Halve the shadow map on touch devices to keep the frame budget in reach.
    const shadowSize = IS_TOUCH ? 1024 : 2048;
    this.sun.shadow.mapSize.set(shadowSize, shadowSize);
    this.sun.shadow.camera.near = 10;
    this.sun.shadow.camera.far = 320;
    const s = 130;
    this.sun.shadow.camera.left = -s;
    this.sun.shadow.camera.right = s;
    this.sun.shadow.camera.top = s;
    this.sun.shadow.camera.bottom = -s;
    this.sun.shadow.bias = -0.0004;
    this.sun.shadow.normalBias = 0.04;
    scene.add(this.sun);
    scene.add(this.sun.target);
  }

  /**
   * mood in [0,1]: 1 = alive/warm, 0 = bleak/grey.
   * night in [0,1]: 0 = day, 1 = night (dims key, cools ambient).
   */
  setMood(mood: number, night: number): void {
    const dim = lerp(0.35, 1.0, mood) * lerp(1.0, 0.4, night);
    this.sun.intensity = 1.15 * dim;
    this.ambient.intensity = lerp(0.18, 0.4, mood) * lerp(1.0, 0.7, night);
    this.hemi.intensity = lerp(0.45, 0.95, mood) * lerp(1.0, 0.6, night);
    this.hemi.color.copy(this.coolSky).lerp(this.warmSky, mood);
  }

  /** Keep the shadow frustum centred on the player so shadows stay crisp nearby. */
  follow(x: number, z: number): void {
    this.sun.position.set(x + 60, 90, z + 40);
    this.sun.target.position.set(x, 0, z);
    this.sun.target.updateMatrixWorld();
  }

  dispose(): void {
    this.sun.dispose();
    this.hemi.dispose();
    this.ambient.dispose();
  }
}
